import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
    Bell, Calendar, FileSignature, GitBranch, MessageSquare, UserPlus, AlertCircle, CheckCheck,
} from "lucide-react";

const EVENT_ICONS = {
    interview_scheduled: Calendar,
    interview_rescheduled: Calendar,
    interview_cancelled: Calendar,
    offer_sent: FileSignature,
    stage_milestone: GitBranch,
    recruiter_assigned: UserPlus,
    ticket_reply: MessageSquare,
    ticket_new: MessageSquare,
    generic: AlertCircle,
};

/**
 * Topbar notification bell — Phase 58.
 *
 * Polls `/api/notifications/unread-count` every 30s (and once on mount).
 * Opening the dropdown lazily fetches the last 10 notifications. Clicking an
 * item marks it read AND navigates to its `link`. Mark-all-read button at the
 * bottom calls `/api/notifications/read-all`.
 *
 * Hidden for unauthenticated users (no user context).
 */
export default function NotificationBell() {
    const [unreadCount, setUnreadCount] = useState(0);
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const nav = useNavigate();
    const lastFetchRef = useRef(0);

    const fetchCount = useCallback(async () => {
        try {
            const { data } = await api.get("/notifications/unread-count");
            setUnreadCount(data?.count || 0);
        } catch { /* silent */ }
    }, []);

    const fetchItems = useCallback(async () => {
        // Throttle to once / 4s when dropdown reopens quickly
        const now = Date.now();
        if (now - lastFetchRef.current < 1500) return;
        lastFetchRef.current = now;
        setLoading(true);
        try {
            const { data } = await api.get("/notifications", { params: { limit: 10 } });
            setItems(data || []);
        } catch { /* silent */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        fetchCount();
        const interval = setInterval(fetchCount, 30000);
        return () => clearInterval(interval);
    }, [fetchCount]);

    useEffect(() => {
        if (open) fetchItems();
    }, [open, fetchItems]);

    const onItemClick = async (n) => {
        try { await api.post(`/notifications/${n.id}/read`); } catch { /* silent */ }
        setOpen(false);
        if (n.link) nav(n.link);
        fetchCount();
    };

    const onMarkAll = async () => {
        try {
            await api.post("/notifications/read-all");
            setItems((arr) => arr.map((n) => ({ ...n, read: true })));
            setUnreadCount(0);
            toast.success("All notifications marked as read");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
                <button
                    className="relative p-1.5 hover:bg-secondary border border-transparent hover:border-border"
                    aria-label="Notifications"
                    title="Notifications"
                    data-testid="notification-bell"
                >
                    <Bell className="w-5 h-5 text-foreground/80" />
                    {unreadCount > 0 ? (
                        <span
                            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-rose-500 text-white text-[10px] font-bold rounded-full inline-flex items-center justify-center"
                            data-testid="notification-bell-count"
                        >
                            {unreadCount > 99 ? "99+" : unreadCount}
                        </span>
                    ) : null}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notification-dropdown">
                <DropdownMenuLabel className="flex items-center justify-between px-3 py-2 border-b border-border bg-secondary/30">
                    <span className="text-[11px] font-mono uppercase tracking-widest">Notifications</span>
                    {unreadCount > 0 ? (
                        <button
                            onClick={onMarkAll}
                            className="text-[10px] font-mono uppercase tracking-widest text-ikb hover:underline inline-flex items-center gap-1"
                            data-testid="notification-mark-all"
                        >
                            <CheckCheck className="w-3 h-3" /> Mark all read
                        </button>
                    ) : null}
                </DropdownMenuLabel>

                <div className="max-h-[360px] overflow-y-auto">
                    {loading && items.length === 0 ? (
                        <div className="px-3 py-6 text-sm text-foreground/60 text-center">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="px-3 py-6 text-sm text-foreground/60 text-center">No notifications yet</div>
                    ) : items.map((n) => {
                        const Icon = EVENT_ICONS[n.event] || EVENT_ICONS.generic;
                        return (
                            <button
                                key={n.id}
                                onClick={() => onItemClick(n)}
                                className={`w-full text-left px-3 py-2.5 hover:bg-secondary/40 border-b border-border last:border-b-0 ${n.read ? "" : "bg-ikb/5"}`}
                                data-testid={`notification-item-${n.id}`}
                            >
                                <div className="flex items-start gap-2">
                                    <div className={`mt-0.5 ${n.read ? "text-foreground/40" : "text-ikb"}`}>
                                        <Icon className="w-4 h-4" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className={`text-sm truncate ${n.read ? "text-foreground/70" : "font-medium text-foreground"}`}>
                                            {n.title}
                                        </div>
                                        <div className="text-[11px] text-foreground/60 truncate mt-0.5">{n.body}</div>
                                        <div className="text-[10px] text-foreground/40 mt-1">{timeAgo(n.created_at)}</div>
                                    </div>
                                    {!n.read ? <span className="w-2 h-2 bg-ikb rounded-full shrink-0 mt-1.5" /> : null}
                                </div>
                            </button>
                        );
                    })}
                </div>

                <DropdownMenuSeparator className="my-0" />
                <Link
                    to="/notifications"
                    onClick={() => setOpen(false)}
                    className="block px-3 py-2 text-center text-[11px] font-mono uppercase tracking-widest text-ikb hover:bg-secondary/40"
                    data-testid="notification-see-all"
                >
                    See all →
                </Link>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function timeAgo(iso) {
    if (!iso) return "";
    try {
        const t = new Date(iso).getTime();
        const diff = (Date.now() - t) / 1000;
        if (diff < 60) return `${Math.max(1, Math.round(diff))}s ago`;
        if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
        return `${Math.round(diff / 86400)}d ago`;
    } catch { return ""; }
}
